/**
 * The default reporter: loud in a console, and nothing more.
 *
 * `console.error` rather than a thrown error. A missing string is a defect
 * worth fixing, and it is not worth taking a tutor's dashboard down for — the
 * key renders in place, which says what is wrong and where.
 */
export const reportToConsole = (problem) => {
    // Reached through `globalThis` rather than the bare global, because this
    // package compiles against ES2023 alone: it runs in a browser, in Node, and
    // in a test runner, and pulling in `dom` or `@types/node` to type one call
    // would put a lie in whichever of the three it did not describe.
    const sink = globalThis.console;
    const detail = problem.missingParamIndex === undefined
        ? `missing translation: ${problem.language}.${problem.key}`
        : `missing interpolation parameter {${problem.missingParamIndex}} for ${problem.language}.${problem.key}`;
    sink?.error?.(`[i18n] ${detail}`);
};
/** Walk a dotted path. Returns the string leaf, or undefined for anything else. */
export function lookup(dictionary, key) {
    let node = dictionary;
    for (const segment of key.split('.')) {
        if (typeof node !== 'object' || node === null)
            return undefined;
        node = node[segment];
    }
    return typeof node === 'string' ? node : undefined;
}
const PLACEHOLDER = /\{(\d+)\}/g;
/**
 * Substitute `{0}`, `{1}`, … from the array, positionally.
 *
 * A placeholder with no parameter is left in the string rather than replaced
 * with an empty span, for the same reason a missing key renders as the key: a
 * sentence with `{1}` visible in it gets fixed, and a sentence with a hole in
 * it ships.
 */
export function interpolate(template, params, onMissingParam) {
    return template.replace(PLACEHOLDER, (placeholder, digits) => {
        const index = Number(digits);
        const value = params[index];
        if (value === undefined) {
            onMissingParam?.(index);
            return placeholder;
        }
        return String(value);
    });
}
/**
 * Bind a dictionary to a `t`.
 *
 * The dictionary is passed in rather than looked up from the language, so this
 * function is pure and a test can hand it a two-key object. `createTranslator`
 * below is the one that knows about the shipped files.
 */
export function createTranslatorFor(language, dictionary, options = {}) {
    const report = options.report ?? reportToConsole;
    return function t(key, params) {
        const template = lookup(dictionary, key);
        if (template === undefined) {
            // No fall back to English. Section 29: a Spanish string that is missing
            // must look missing, not look finished in the wrong language.
            report({ language, key });
            return key;
        }
        if (!params || params.length === 0) {
            return interpolate(template, [], (index) => report({ language, key, missingParamIndex: index }));
        }
        return interpolate(template, params, (index) => report({ language, key, missingParamIndex: index }));
    };
}
//# sourceMappingURL=translate.js.map